import { AddDiv, AddDomElement, CreateDomElement, ClearDomElement, ShowDomElement } from '../engine/viewer/domutils.js';
import { CommentsManager, Comment, CadReference } from './comments.js';
import { SidebarPanel } from './sidebarpanel.js';
import { Loc } from '../engine/core/localization.js';
import { debugLog } from '../../tools/debug.js';

// Individual comment component
export class CommentComponent
{
    constructor (comment, commentsManager, onReply, onEdit, onDelete, onCadReferenceClick, onToggleReplies)
    {
        debugLog('Creating new CommentComponent');
        this.comment = comment;
        this.commentsManager = commentsManager;
        this.onReply = onReply;
        this.onEdit = onEdit;
        this.onDelete = onDelete;
        this.onCadReferenceClick = onCadReferenceClick;
        this.onToggleReplies = onToggleReplies;
        this.element = null;
        this.isEditing = false;
        this.repliesCount = 0;
        this.areRepliesCollapsed = false;
        this.closeButton = null; // For viewing state close button
    }

    createElement ()
    {
        debugLog('Creating comment element');
        const user = this.commentsManager.getUser(this.comment.userId);
        const currentUser = this.commentsManager.getCurrentUser();
        const isOwnComment = currentUser && this.comment.userId === currentUser.id;

        this.element = CreateDomElement('div', 'ov_comment' + (this.comment.isReply() ? ' ov_comment_reply' : ''));
        this.element.setAttribute('data-comment-id', this.comment.id);
        // Comment header
        const header = AddDiv(this.element, 'ov_comment_header');

        // User avatar and info
        const userInfo = AddDiv(header, 'ov_comment_user_info');
        const avatar = AddDiv(userInfo, 'ov_comment_avatar');
        avatar.textContent = user ? user.displayName.charAt(0).toUpperCase() : '?';
        if (user && user.isOnline) {
            avatar.classList.add('online');
        }

        const userDetails = AddDiv(userInfo, 'ov_comment_user_details');
        AddDiv(userDetails, 'ov_comment_username', user ? user.displayName : Loc('Amane Hosanna'));
        AddDiv(userDetails, 'ov_comment_timestamp', this.comment.getFormattedTimestamp());

        // Comment actions
        if (isOwnComment) {
            debugLog('Adding comment actions for own comment');
            const actions = AddDiv(header, 'ov_comment_actions');
            const editBtn = AddDiv(actions, 'ov_comment_action', '✏️');
            editBtn.title = Loc('Edit');
            editBtn.addEventListener('click', () => this.startEditing());

            const deleteBtn = AddDiv(actions, 'ov_comment_action', '🗑️');
            deleteBtn.title = Loc('Delete');
            deleteBtn.addEventListener('click', () => this.onDelete(this.comment.id));
        }

                // CAD state indicator (check data field for CAD state)
        const hasCADState = (this.comment.data?.camera) ||
                           (this.comment.data?.cadReference?.cadState) ||
                           (this.comment.cadReference?.type === 'cad_state' && this.comment.cadReference.cadState);

        if (hasCADState) {
            debugLog('Adding CAD state indicator to comment');
            const cadRef = AddDiv(this.element, 'ov_comment_cad_reference');

            const navIcon = AddDomElement(cadRef, 'span', 'ov_comment_cad_nav_icon', '📍');
            const navText = AddDomElement(cadRef, 'span', 'ov_comment_cad_text', 'View CAD State');

            cadRef.addEventListener('click', async () => {
                debugLog('CAD state navigation clicked for comment', this.comment.id);
                if (this.onCadReferenceClick) {
                    // Create a reference object for the click handler
                    const cadReference = {
                        type: 'cad_state',
                        commentId: this.comment.id,
                        cadState: this.comment.data?.cadReference?.cadState || this.comment.cadReference?.cadState || null,
                        data: this.comment.data || null
                    };
                    this.onCadReferenceClick(cadReference);
                }
            });
            cadRef.classList.add('clickable', 'ov_comment_cad_state');
            cadRef.title = 'Click to view the CAD state when this comment was created';
        }

                // Legacy CAD reference handling (for non-CAD state references)
        // Check both data.cadReference and old cadReference field
        const legacyCadRef = this.comment.data?.cadReference || this.comment.cadReference;
        if (legacyCadRef && legacyCadRef.type !== 'cad_state') {
            debugLog('Adding legacy CAD reference to comment');
            const cadRef = AddDiv(this.element, 'ov_comment_cad_reference');

            const refIcon = AddDomElement(cadRef, 'span', 'ov_comment_cad_icon', '🔗');
            const refText = AddDomElement(cadRef, 'span', 'ov_comment_cad_text',
                `${legacyCadRef.type}: ${legacyCadRef.elementName || 'Unknown'}`);

            cadRef.addEventListener('click', () => {
                debugLog('CAD reference clicked');
                if (this.onCadReferenceClick) {
                    this.onCadReferenceClick(legacyCadRef);
                }
            });
            cadRef.classList.add('clickable');
        }

        // Comment content
        this.contentDiv = AddDiv(this.element, 'ov_comment_content');
        this.renderContent();

        // Comment footer
        const footer = AddDiv(this.element, 'ov_comment_footer');

        if (this.comment.isEdited) {
            AddDiv(footer, 'ov_comment_edited', Loc('(edited)'));
        }

        if (!this.comment.isReply()) {
            const replyBtn = AddDiv(footer, 'ov_comment_reply_btn', Loc('Reply'));
            replyBtn.addEventListener('click', () => {
                debugLog('Reply button clicked');
                this.onReply(this.comment.id);
            });

            // Store footer for later use when adding toggle button
            this.footerDiv = footer;
        }

        return this.element;
    }

    renderContent ()
    {
        debugLog('Rendering comment content', this.comment ?? '--');
        ClearDomElement(this.contentDiv);

        if (this.isEditing) {
            debugLog('Rendering edit mode content');
            const editContainer = AddDiv(this.contentDiv, 'ov_comment_edit_container');
            const editInput = AddDomElement(editContainer, 'textarea', 'ov_comment_edit_input');
            editInput.value = this.comment.message;
            editInput.rows = 3;

            const editActions = AddDiv(editContainer, 'ov_comment_edit_actions');
            const saveBtn = AddDiv(editActions, 'ov_button ov_button_small', Loc('Save'));
            const cancelBtn = AddDiv(editActions, 'ov_button ov_button_small ov_button_outline', Loc('Cancel'));

            saveBtn.addEventListener('click', () => {
                debugLog('Save edit button clicked');
                if (editInput.value.trim()) {
                    this.onEdit(this.comment.id, editInput.value.trim());
                    this.isEditing = false;
                    this.renderContent();
                }
            });

            cancelBtn.addEventListener('click', () => {
                debugLog('Cancel edit button clicked');
                this.isEditing = false;
                this.renderContent();
            });

            editInput.focus();
        } else {
            debugLog('Rendering normal content', this.comment);
            const messageDiv = AddDiv(this.contentDiv, 'ov_comment_message');
            messageDiv.innerHTML = this.formatMessage(this.comment.message);
        }
    }

    formatMessage (message)
    {
        debugLog('Formatting message', message);
        // Format @mentions
        let formatted = message?.replace(/@(\w+)/g, '<span class="ov_comment_mention">@$1</span>');

        // Convert line breaks
        formatted = formatted?.replace(/\n/g, '<br>');

        return formatted;
    }

    startEditing ()
    {
        debugLog('Starting comment edit');
        this.isEditing = true;
        this.renderContent();
    }

    update ()
    {
        debugLog('Updating comment');
        if (this.element) {
            // Update timestamp
            const timestampElement = this.element.querySelector('.ov_comment_timestamp');
            if (timestampElement) {
                timestampElement.textContent = this.comment.getFormattedTimestamp();
            }

            // Update content if not editing
            if (!this.isEditing) {
                this.renderContent();
            }
        }
    }

    setRepliesCount (count, collapsed = false)
    {
        debugLog(`Setting replies count to ${count}, collapsed: ${collapsed}`);
        this.repliesCount = count;
        this.areRepliesCollapsed = collapsed;

        // Create toggle button if it doesn't exist and we have replies
        if (count > 0 && !this.toggleRepliesBtn && this.footerDiv) {

            // ! TODO fix toggle bug
            this.toggleRepliesBtn = AddDiv(this.footerDiv, 'ov_comment_toggle_replies_btn');
            this.toggleRepliesBtn.addEventListener('click', () => {
                debugLog('Toggle replies button clicked');
                if (this.onToggleReplies) {
                    this.onToggleReplies(this.comment.id);
                }
            });
        }

        // Remove toggle button if no replies
        if (count === 0 && this.toggleRepliesBtn) {
            this.toggleRepliesBtn.remove();
            this.toggleRepliesBtn = null;
        }

        this.updateToggleButton();
    }

    updateToggleButton ()
    {
        if (this.toggleRepliesBtn && this.repliesCount > 0) {
            const icon = this.areRepliesCollapsed ? '▶' : '▼';
            const text = this.repliesCount === 1 ? 'reply' : 'replies';
            this.toggleRepliesBtn.textContent = `${icon} ${this.repliesCount} ${Loc(text)}`;
            this.toggleRepliesBtn.title = this.areRepliesCollapsed ? Loc('Show replies') : Loc('Hide replies');
        }
    }

        /**
     * Set visual indicator when comment is being viewed
     */
    setViewing(isViewing) {
        if (!this.element) return;

        if (isViewing) {
            this.element.classList.add('ov_comment_viewing');

            // Add close button if not already present
            if (!this.closeButton) {
                this.closeButton = CreateDomElement('div', 'ov_comment_viewing_close');
                // this.closeButton.textContent = 'Close';
                this.closeButton.textContent = 'X';
                this.closeButton.addEventListener('click', (e) => {
                    e.stopPropagation(); // Prevent triggering other click handlers
                    debugLog('Close button clicked for comment:', this.comment.id);

                    // Call the CAD reference click handler to stop viewing
                    if (this.onCadReferenceClick) {
                        const cadReference = {
                            type: 'cad_state',
                            commentId: this.comment.id,
                            cadState: this.comment.data?.cadReference?.cadState || this.comment.cadReference?.cadState || null,
                            data: this.comment.data || null
                        };
                        this.onCadReferenceClick(cadReference);
                    }
                });
                this.element.appendChild(this.closeButton);
            }

            debugLog('Set comment as viewing:', this.comment.id);
        } else {
            this.element.classList.remove('ov_comment_viewing');

            // Remove close button
            if (this.closeButton) {
                this.closeButton.remove();
                this.closeButton = null;
            }

            debugLog('Removed viewing state from comment:', this.comment.id);
        }
    }

    /**
     * Check if this comment is currently being viewed
     */
    isViewing() {
        return this.element?.classList.contains('ov_comment_viewing') || false;
    }
}

// Comment composer for new comments and replies
export class CommentComposer
{
    constructor (commentsManager, onSubmit, {parentId = null, cadReference = null, showCancel = false} = {})
    {
        debugLog('Creating new CommentComposer');
        this.commentsManager = commentsManager;
        this.onSubmit = onSubmit;
        this.parentId = parentId;
        this.cadReference = cadReference;
        this.showCancel = showCancel; // Force show cancel button even for non-replies
        this.element = null;

        // Mention functionality
        this.mentionDropdown = null;
        this.currentMentionQuery = '';
        this.mentionStartPos = -1;
        this.selectedMentionIndex = -1;
    }

    createElement ()
    {
        debugLog('Creating composer element');
        this.element = CreateDomElement('div', 'ov_comment_composer');

        // CAD reference display (if any)
        if (this.cadReference) {
            debugLog('Adding CAD reference to composer');
            const cadRefDisplay = AddDiv(this.element, 'ov_comment_composer_cad_ref');
            AddDomElement(cadRefDisplay, 'span', null, `📎 ${this.cadReference.type}: ${this.cadReference.elementName}`);
            const removeBtn = AddDomElement(cadRefDisplay, 'button', 'ov_comment_composer_remove_ref', '×');
            removeBtn.addEventListener('click', () => {
                debugLog('Removing CAD reference');
                this.cadReference = null;
                this.element.removeChild(cadRefDisplay);
            });
        }

        this.textAreaParent = AddDiv(this.element, 'ov_display_flex_column');
        // Text input
        // this.textArea = AddDomElement(this.element, 'textarea', 'ov_comment_composer_input');
        this.textArea = AddDomElement(this.textAreaParent, 'textarea', 'ov_comment_composer_input');
        this.textArea.placeholder = this.parentId ? Loc('Write a reply...') : Loc('Add a comment...');
        this.textArea.rows = 3;

        // Actions
        const actions = AddDiv(this.element, 'ov_comment_composer_actions');

        const userScrollDiv = AddDiv(actions, 'ov_overflow_x_auto');
        // User list for mentions
        // const userList = AddDiv(actions, 'ov_comment_composer_users');
        const userList = AddDiv(userScrollDiv, 'ov_comment_composer_users ov_thin_scrollbar');
        this.createUserMentionButtons(userList);

        const rightActions = AddDiv(actions, 'ov_comment_composer_right_actions');


        // Submit button
        const submitBtn = AddDiv(rightActions, 'ov_button ov_button_small', this.parentId ? Loc('Reply') : Loc('Comment'));
        submitBtn.addEventListener('click', () => this.submitComment());


        // Cancel button (for replies or when explicitly requested)
        if (this.parentId || this.showCancel) {
            const cancelBtn = AddDiv(rightActions, 'ov_button ov_button_small ov_button_outline', Loc('Cancel'));
            cancelBtn.addEventListener('click', () => {
                debugLog('Cancel clicked');
                if (this.onSubmit) {
                    this.onSubmit(null); // Signal cancellation
                }
            });
        }

        // Handle keyboard input for mentions and submit
        this.textArea.addEventListener('keydown', (e) => {
            // Handle mention dropdown navigation
            if (this.mentionDropdown && this.mentionDropdown.style.display !== 'none') {
                if (e.key === 'Tab' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    this.navigateMentions(1);
                    return;
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    this.navigateMentions(-1);
                    return;
                } else if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    this.selectCurrentMention();
                    return;
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    this.hideMentionDropdown();
                    return;
                }
            }

            // Handle Enter key for submit (Shift+Enter for new line)
            if (e.key === 'Enter' && !e.shiftKey) {
                debugLog('Enter key pressed for submit', this.textArea.value);
                e.preventDefault();
                this.submitComment();
            }
        });

        // Handle input for mention detection
        this.textArea.addEventListener('input', (e) => {
            this.handleMentionInput(e);
        });

        // Hide dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (this.mentionDropdown && !this.element.contains(e.target)) {
                this.hideMentionDropdown();
            }
        });

        return this.element;
    }

    createUserMentionButtons (container)
    {
        debugLog('Creating user mention buttons');
        const currentUser = this.commentsManager?.getCurrentUser();
        Array.from(this.commentsManager?.users?.values()).forEach(user => {
            if (user.id !== currentUser?.id) {
                const userBtn = AddDiv(container, 'ov_comment_user_mention', `@${user.username}`);
                userBtn.title = user.displayName;
                if (user.isOnline) {
                    userBtn.classList.add('online');
                }
                userBtn.addEventListener('click', () => {
                    debugLog(`User mention clicked: @${user.username}`);
                    const currentText = this.textArea.value;
                    const cursorPos = this.textArea.selectionStart;
                    const beforeCursor = currentText.substring(0, cursorPos);
                    const afterCursor = currentText.substring(cursorPos);

                    // Add space before @ if needed
                    const prefix = beforeCursor.length > 0 && !beforeCursor.endsWith(' ') ? ' ' : '';
                    const suffix = ' ';

                    this.textArea.value = beforeCursor + prefix + `@${user.username}` + suffix + afterCursor;
                    this.textArea.focus();
                    this.textArea.setSelectionRange(cursorPos + prefix.length + user.username.length + 2, cursorPos + prefix.length + user.username.length + 2);
                });
            }
        });
    }

    submitComment ()
    {
        const message = this.textArea.value.trim();

        debugLog('Submitting comment', message);
        if (message && this.onSubmit) {
            // Store cadReference in data field if it exists
            const submitData = {
                message,
                parentId: this.parentId
            };

            if (this.cadReference) {
                submitData.data = {
                    cadReference: this.cadReference
                };
            }

            this.onSubmit(submitData);
            this.textArea.value = '';
        }
    }

    focus ()
    {
        debugLog('Focusing composer');
        if (this.textArea) {
            this.textArea.focus();
        }
    }

    setCadReference (cadReference)
    {
        debugLog('Setting CAD reference');
        this.cadReference = cadReference;
        // Re-create element to show CAD reference
        if (this.element && this.element.parentNode) {
            const parent = this.element.parentNode;
            const newElement = this.createElement();
            parent.replaceChild(newElement, this.element);
        }
    }

    /**
     * Handle input for mention detection and dropdown display
     */
    handleMentionInput(e) {
        const text = this.textArea.value;
        const cursorPos = this.textArea.selectionStart;

        // Find @ symbol before cursor
        let atPos = -1;
        for (let i = cursorPos - 1; i >= 0; i--) {
            if (text[i] === '@') {
                atPos = i;
                break;
            } else if (text[i] === ' ' || text[i] === '\n') {
                break; // Stop if we hit whitespace before finding @
            }
        }

        if (atPos !== -1) {
            // Extract mention query
            const mentionQuery = text.substring(atPos + 1, cursorPos);

            // Only show dropdown if query is reasonable (not too long, no spaces)
            if (mentionQuery.length <= 20 && !mentionQuery.includes(' ') && !mentionQuery.includes('\n')) {
                this.currentMentionQuery = mentionQuery;
                this.mentionStartPos = atPos;
                this.showMentionDropdown(mentionQuery);
                return;
            }
        }

        // Hide dropdown if no valid mention context
        this.hideMentionDropdown();
    }

    /**
     * Show mention dropdown with filtered users
     */
    showMentionDropdown(query) {
        if (!this.commentsManager?.users) return;

        // Filter users based on query
        const users = Array.from(this.commentsManager.users.values());
        const filteredUsers = users.filter(user => {
            if (!query) return true; // Show all users if no query
            return user.username?.toLowerCase().includes(query.toLowerCase()) ||
                   user.displayName?.toLowerCase().includes(query.toLowerCase());
        }).slice(0, 5); // Limit to 5 users

        if (filteredUsers.length === 0) {
            this.hideMentionDropdown();
            return;
        }

        // Create dropdown if it doesn't exist
        if (!this.mentionDropdown) {
            this.mentionDropdown = CreateDomElement('div', 'ov_mention_dropdown');
            this.element.appendChild(this.mentionDropdown);
        }

        // Clear and populate dropdown
        this.mentionDropdown.innerHTML = '';
        this.selectedMentionIndex = 0;

        filteredUsers.forEach((user, index) => {
            const userItem = CreateDomElement('div', 'ov_mention_item');
            if (index === 0) userItem.classList.add('ov_mention_selected');

            const avatar = CreateDomElement('div', 'ov_mention_avatar');
            avatar.textContent = user.displayName?.charAt(0).toUpperCase() || '?';

            const userInfo = CreateDomElement('div', 'ov_mention_user_info');
            const displayName = CreateDomElement('div', 'ov_mention_display_name');
            displayName.textContent = user.displayName || user.username || 'Unknown';
            const username = CreateDomElement('div', 'ov_mention_username');
            username.textContent = `@${user.username || 'unknown'}`;

            userInfo.appendChild(displayName);
            userInfo.appendChild(username);
            userItem.appendChild(avatar);
            userItem.appendChild(userInfo);

            // Add click handler
            userItem.addEventListener('click', () => {
                this.insertMention(user);
            });

            this.mentionDropdown.appendChild(userItem);
        });

        // Position dropdown below cursor
        this.positionMentionDropdown();
        this.mentionDropdown.style.display = 'block';
    }

    /**
     * Position the mention dropdown below the cursor
     */
    positionMentionDropdown() {
        if (!this.mentionDropdown) return;

        // Get textarea position and cursor position
        const textareaRect = this.textArea.getBoundingClientRect();
        const elementRect = this.element.getBoundingClientRect();

        // Position dropdown below textarea for simplicity
        // In a more advanced implementation, you could calculate exact cursor position
        this.mentionDropdown.style.position = 'absolute';
        this.mentionDropdown.style.bottom = (textareaRect.bottom - elementRect.top + 5) + 'px';
        this.mentionDropdown.style.left = '10px';
        this.mentionDropdown.style.zIndex = '1000';
    }

    /**
     * Navigate through mention suggestions
     */
    navigateMentions(direction) {
        if (!this.mentionDropdown) return;

        const items = this.mentionDropdown.querySelectorAll('.ov_mention_item');
        if (items.length === 0) return;

        // Remove current selection
        items[this.selectedMentionIndex]?.classList.remove('ov_mention_selected');

        // Update selection index
        this.selectedMentionIndex = Math.max(0, Math.min(items.length - 1,
            this.selectedMentionIndex + direction));

        // Add selection to new item
        items[this.selectedMentionIndex]?.classList.add('ov_mention_selected');
    }

    /**
     * Select the currently highlighted mention
     */
    selectCurrentMention() {
        if (!this.mentionDropdown) return;

        const selectedItem = this.mentionDropdown.querySelector('.ov_mention_selected');
        if (selectedItem) {
            selectedItem.click();
        }
    }

    /**
     * Insert mention into textarea
     */
    insertMention(user) {
        const text = this.textArea.value;
        const username = user.username || 'unknown';
        const mention = `@${username}`;

        // Replace the partial mention with the complete mention
        const beforeMention = text.substring(0, this.mentionStartPos);
        const afterCursor = text.substring(this.textArea.selectionStart);
        const newText = beforeMention + mention + ' ' + afterCursor;

        this.textArea.value = newText;

        // Position cursor after the mention
        const newCursorPos = this.mentionStartPos + mention.length + 1;
        this.textArea.setSelectionRange(newCursorPos, newCursorPos);

        // Hide dropdown and reset state
        this.hideMentionDropdown();
        this.textArea.focus();

        debugLog('Inserted mention:', mention);
    }

    /**
     * Hide the mention dropdown
     */
    hideMentionDropdown() {
        if (this.mentionDropdown) {
            this.mentionDropdown.style.display = 'none';
        }
        this.currentMentionQuery = '';
        this.mentionStartPos = -1;
        this.selectedMentionIndex = -1;
    }
}

// Main sidebar comments panel
export class SidebarCommentsPanel extends SidebarPanel
{
    constructor (parentDiv, apiConfig = null, globalCommentsManager = null)
    {
        debugLog('Creating SidebarCommentsPanel');
        super(parentDiv, 'ov_sidebar_panel ov_comments_panel');

        // Use global comments manager if provided, otherwise create local one
        this.globalCommentsManager = globalCommentsManager;
        this.commentsManager = globalCommentsManager ? globalCommentsManager.commentsManager : new CommentsManager(apiConfig);

        this.commentComponents = new Map();
        this.replyComposer = null;
        this.selectedCadReference = null;
        this.collapsedReplies = new Set(); // Track which comment replies are collapsed
        this.explicitlyExpandedReplies = new Set(); // Track which replies have been explicitly expanded

        // Setup reactive subscriptions if global manager available
        this.subscriptions = [];

        this.initializeUI();
        this.setupEventListeners();
        this.setupReactiveSubscriptions();
        debugLog('SidebarCommentsPanel created');
    }

    GetName ()
    {
        return Loc('Comments');
    }

    GetIcon ()
    {
        return 'feedback';
        // return 'comments';
    }

    HasTitle ()
    {
        return true;
    }
    // #Override
    Show(show) {
        if (this.visible === show) {
            return;
        }

        this.visible = show;
        ShowDomElement(this.panelDiv, this.visible, 'flex');
    }

    initializeUI ()
    {
        debugLog('Initializing comments panel UI');


        // Check if user is authenticated
        const currentUser = this.commentsManager?.getCurrentUser();
        if (!currentUser) {
            this.showLoginRequired();
            return;
        }

        // Comments container (only show when authenticated)
        this.commentsContainer = AddDiv(this.contentDiv, 'ov_comments_container ov_thin_scrollbar');
        this.commentsContainer.classList.add('ov_flex_grow');
        this.commentsContainer.classList.add('ov_overflow_x_hidden');

        // Comment composer (only show when authenticated)
        this.composerContainer = AddDiv(this.contentDiv, 'ov_comments_composer_container');
        this.mainComposer = new CommentComposer(this.commentsManager, async (data) => {
            if (data) {
                debugLog('Main composer submitting comment', data);
                try {
                    const commentData = {
                        userId: this.commentsManager.getCurrentUser()?.id,
                        message: data.message,
                        parentId: data.parentId
                    };

                    // Merge any data field from the composer
                    if (data.data) {
                        commentData.data = data.data;
                    }

                    await this.commentsManager.addComment(commentData);
                } catch (error) {
                    console.error('Failed to add comment:', error);
                    // Show error to user
                    this.showErrorMessage('Failed to add comment. Please try again.');
                }
            }
        });

        if (this.composerContainer && this.mainComposer) {
            this.composerContainer.appendChild(this.mainComposer.createElement());
        }

        this.renderComments();
    }

    createHeader ()
    {
        debugLog('Creating header');
        ClearDomElement(this.headerDiv);

        const currentUser = this.commentsManager?.getCurrentUser();
        // if (currentUser) {
        //     const userInfo = AddDiv(this.headerDiv, 'ov_comments_current_user');
        //     const avatar = AddDiv(userInfo, 'ov_comment_avatar online');
        //     avatar.textContent = currentUser.displayName.charAt(0).toUpperCase();
        //     AddDiv(userInfo, 'ov_comments_current_user_name', currentUser.displayName);
        // }

        // Online users count
        // const onlineUsers = Array.from(this.commentsManager?.users?.values()).filter(u => u.isOnline);
        // const onlineCount = AddDiv(this.headerDiv, 'ov_comments_online_count');
        // onlineCount.textContent = `${onlineUsers.length} ${Loc('online')}`;

    }

    setupEventListeners ()
    {
        debugLog('Setting up event listeners');
        this.commentsManager.addEventListener('commentAdded', (data) => {
            debugLog('Comment added event received');

            // If this is a reply, ensure the parent comment's replies are expanded
            if (data.comment && data.comment.parentId) {
                debugLog(`New reply added to comment ${data.comment.parentId}, expanding replies`);
                this.collapsedReplies.delete(data.comment.parentId);
                this.explicitlyExpandedReplies.add(data.comment.parentId);
            }

            this.renderComments();

            // Scroll to show the new comment/reply in context
            if (data.comment && data.comment.parentId) {
                // For replies, scroll to show the context (may not need to scroll to bottom)
                setTimeout(() => this.scrollToComment(data.comment.id), 100);
            } else {
                // For top-level comments, scroll to bottom
                this.scrollToBottom();
            }
            console.log(">> addedComment", data)
            this.highlightComment(data.comment.id);
        });

        this.commentsManager.addEventListener('commentEdited', () => {
            debugLog('Comment edited event received');
            this.renderComments();
        });

        this.commentsManager.addEventListener('commentDeleted', () => {
            debugLog('Comment deleted event received');
            this.renderComments();
        });

        this.commentsManager.addEventListener('usersMentioned', (data) => {
            debugLog('Users mentioned event received');
            // Handle mention notifications
            console.log('Users mentioned:', data.mentions);
        });

        this.commentsManager.addEventListener('commentsLoaded', (data) => {
            debugLog(`Comments loaded from ${data.source}`);
            this.refreshUI(); // Refresh UI in case authentication state changed
        });

        this.commentsManager.addEventListener('commentsRefreshed', (data) => {
            debugLog(`Comments refreshed from ${data.source}`);
            this.renderComments();
            this.updateConnectionStatus();
        });

        // Listen for authentication state changes
        this.commentsManager.addEventListener('currentUserChanged', (data) => {
            debugLog('Current user changed, refreshing UI');
            this.refreshUI();
        });

        // Update status when network changes
        window.addEventListener('online', () => {
            this.updateConnectionStatus();
        });

        window.addEventListener('offline', () => {
            this.updateConnectionStatus();
        });
    }

    setupReactiveSubscriptions ()
    {
        if (!this.globalCommentsManager) return;

        debugLog('Setting up reactive subscriptions');

        // Subscribe to comments changes
        const commentsUnsubscribe = this.globalCommentsManager.subscribe('comments', (newComments, oldComments) => {
            debugLog('Comments signal updated', newComments);
            this.renderComments();
        });
        this.subscriptions.push(commentsUnsubscribe);

        // Subscribe to active comment changes
        const activeCommentUnsubscribe = this.globalCommentsManager.subscribe('activeComment', (activeComment, oldComment) => {
            debugLog('Active comment changed', activeComment);
            this.highlightActiveComment(activeComment);
        });
        this.subscriptions.push(activeCommentUnsubscribe);

        // Subscribe to comment mode changes
        const commentModeUnsubscribe = this.globalCommentsManager.subscribe('isCommentModeActive', (isActive, wasActive) => {
            debugLog('Comment mode changed', isActive);
            this.updateCommentModeUI(isActive);
        });
        this.subscriptions.push(commentModeUnsubscribe);
    }

    highlightActiveComment (activeComment)
    {
        // Remove previous highlights
        const previousHighlights = this.commentsContainer.querySelectorAll('.ov_comment_active');
        previousHighlights.forEach(el => el.classList.remove('ov_comment_active'));

        if (activeComment) {
            const commentElement = this.commentsContainer.querySelector(`[data-comment-id="${activeComment.id}"]`);
            if (commentElement) {
                commentElement.classList.add('ov_comment_active');
                commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }

    updateCommentModeUI (isActive)
    {
        if (isActive) {
            this.contentDiv.classList.add('ov_comment_mode_active');
        } else {
            this.contentDiv.classList.remove('ov_comment_mode_active');
        }
    }

    renderComments ()
    {
        debugLog('Rendering comments');
        if (!this.commentsContainer) {
            debugLog('No comments container available, skipping render');
            return;
        }

        // Check if user is authenticated before rendering comments
        const currentUser = this.commentsManager?.getCurrentUser();
        if (!currentUser) {
            debugLog('No current user, showing login required instead of comments');
            return;
        }

        ClearDomElement(this.commentsContainer);
        this.commentComponents.clear();

        const topLevelComments = this.commentsManager?.getTopLevelComments();

        if (topLevelComments.length === 0) {
            debugLog('No comments to display, showing empty state');
            const emptyState = AddDiv(this.commentsContainer, 'ov_comments_empty_state');
            AddDiv(emptyState, 'ov_comments_empty_icon', '💬');
            AddDiv(emptyState, 'ov_comments_empty_text', Loc('No comments yet. Start the conversation!'));
            return;
        }

        topLevelComments.forEach(comment => {
            this.renderComment(comment);

            // Render replies
            const replies = this.commentsManager.getReplies(comment.id);
            if (replies.length > 0) {
                debugLog(`Rendering ${replies.length} replies for comment ${comment.id}`);
                const repliesContainer = AddDiv(this.commentsContainer, 'ov_comments_replies');
                repliesContainer.setAttribute('data-comment-id', comment.id);

                // Check if replies should be collapsed
                // Auto-collapse if many replies (5+) and not explicitly expanded
                const hasBeenExplicitlyExpanded = this.explicitlyExpandedReplies.has(comment.id);
                const shouldAutoCollapse = replies.length >= 5 && !hasBeenExplicitlyExpanded;
                const isManuallyCollapsed = this.collapsedReplies.has(comment.id);
                const isCollapsed = shouldAutoCollapse || isManuallyCollapsed;

                if (isCollapsed) {
                    repliesContainer.style.display = 'none';
                }

                replies.forEach(reply => {
                    this.renderComment(reply, repliesContainer);
                });

                // Update the toggle button on the parent comment
                const commentComponent = this.commentComponents.get(comment.id);
                if (commentComponent) {
                    commentComponent.setRepliesCount(replies.length, isCollapsed);
                }
            }
        });
    }

    renderComment (comment, container = null)
    {
        debugLog(`Rendering comment ${comment.id}`);
        const targetContainer = container || this.commentsContainer;

        const commentComponent = new CommentComponent(
            comment,
            this.commentsManager,
            (parentId) => this.showReplyComposer(parentId),
            async (commentId, newMessage) => {
                try {
                    await this.commentsManager.editComment(commentId, newMessage);
                } catch (error) {
                    console.error('Failed to edit comment:', error);
                    this.showErrorMessage('Failed to edit comment. Please try again.');
                }
            },
            async (commentId) => {
                try {
                    await this.commentsManager.deleteComment(commentId);
                } catch (error) {
                    console.error('Failed to delete comment:', error);
                    this.showErrorMessage('Failed to delete comment. Please try again.');
                }
            },
            (cadRef) => this.handleCadReferenceClick(cadRef),
            (commentId) => this.toggleReplies(commentId)
        );

        this.commentComponents.set(comment.id, commentComponent);
        targetContainer.appendChild(commentComponent.createElement());
    }

    showReplyComposer (parentId)
    {
        debugLog(`Showing reply composer for comment ${parentId}`);
        // Remove existing reply composer
        if (this.replyComposer) {
            this.replyComposer.element.remove();
            this.replyComposer = null;
        }

        // Create new reply composer
        this.replyComposer = new CommentComposer(
            this.commentsManager,
            async (data) => {
                if (data) {
                    debugLog('Reply composer submitting comment', data);
                    try {
                        const commentData = {
                            userId: this.commentsManager.getCurrentUser()?.id,
                            message: data.message,
                            parentId: data.parentId
                        };

                        // Merge any data field from the composer
                        if (data.data) {
                            commentData.data = data.data;
                        }

                        await this.commentsManager.addComment(commentData);
                    } catch (error) {
                        console.error('Failed to add reply:', error);
                        this.showErrorMessage('Failed to add reply. Please try again.');
                    }
                }
                // Remove reply composer
                if (this.replyComposer) {
                    this.replyComposer.element.remove();
                    this.replyComposer = null;
                }
            },
            { parentId }
        );

        // Find the comment and insert reply composer after it
        const commentComponent = this.commentComponents.get(parentId);
        if (commentComponent && commentComponent.element) {
            const replyElement = this.replyComposer.createElement();
            replyElement.classList.add('ov_comment_reply_composer');

            // Insert after the comment or its replies
            let insertAfter = commentComponent.element;
            const nextSibling = insertAfter.nextSibling;
            if (nextSibling && nextSibling.classList.contains('ov_comments_replies')) {
                insertAfter = nextSibling;
            }

            insertAfter.parentNode.insertBefore(replyElement, insertAfter.nextSibling);
            this.replyComposer.focus();
        }
    }

    showCadReferenceDialog ()
    {
        debugLog('Showing CAD reference dialog');
        // This is a placeholder for CAD component selection
        // In a real implementation, this would open a dialog to select CAD components
        const demoReference = new CadReference('mesh', 'demo_mesh', 'Demo Component');
        this.selectedCadReference = demoReference;
        this.mainComposer.setCadReference(demoReference);
    }

    async handleCadReferenceClick (cadReference)
    {
        debugLog('Handling CAD reference click');
        console.log('CAD reference clicked:', cadReference);

        // Handle CAD state navigation if global manager is available
        if (this.globalCommentsManager && cadReference.type === 'cad_state') {
            try {
                // Check if we're already viewing this comment
                const currentViewing = this.globalCommentsManager.getCurrentViewingComment();
                if (currentViewing && currentViewing.id === cadReference.commentId) {
                    debugLog('Already viewing this comment, stopping view');
                    await this.globalCommentsManager.stopViewingComment(true);
                    this.updateCommentViewingStates();
                    return;
                }

                // Navigate to the comment using the GlobalCommentsManager
                if (cadReference.commentId) {
                    debugLog('Navigating to comment CAD state:', cadReference.commentId);
                    await this.globalCommentsManager.navigateToComment(cadReference.commentId, true);
                    this.updateCommentViewingStates();
                } else {
                    // Legacy handling for old cadReference format
                    debugLog('Using legacy CAD state navigation');
                    await this.globalCommentsManager.restoreCADState(cadReference.cadState, true);

                    // Find the comment with this CAD reference and set it as active
                    const comment = Array.from(this.commentsManager.comments.values())
                        .find(c => c.cadReference === cadReference);
                    if (comment) {
                        this.globalCommentsManager.signals.activeComment.set(comment);
                    }
                }
            } catch (error) {
                console.error('Failed to navigate to CAD state:', error);
                this.showErrorMessage('Failed to navigate to CAD state. Please try again.');
            }
        }

        // Fire event for the main application to handle (legacy support)
        if (this.callbacks && this.callbacks.onCadReferenceClick) {
            this.callbacks.onCadReferenceClick(cadReference);
        }
    }

    /**
     * Update visual indicators for comment viewing states
     */
    updateCommentViewingStates() {
        const currentViewing = this.globalCommentsManager?.getCurrentViewingComment();

        // Update all comment components
        for (const [commentId, component] of this.commentComponents) {
            const isViewing = currentViewing && currentViewing.id === commentId;
            component.setViewing(isViewing);
        }

        debugLog('Updated comment viewing states, currently viewing:', currentViewing?.id || 'none');
    }

    scrollToBottom ()
    {
        debugLog('Scrolling to bottom');
        setTimeout(() => {
            if (this.commentsContainer) {
                this.commentsContainer.scrollTop = this.commentsContainer.scrollHeight;
            }
        }, 100);
    }

    scrollToComment (commentId)
    {
        debugLog(`Scrolling to comment ${commentId}`);
        setTimeout(() => {
            const commentComponent = this.commentComponents.get(commentId);
            if (commentComponent && commentComponent.element && this.commentsContainer) {
                const commentElement = commentComponent.element;
                const containerRect = this.commentsContainer.getBoundingClientRect();
                const elementRect = commentElement.getBoundingClientRect();

                // Check if element is already in view
                const isInView = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;

                if (!isInView) {
                    // Scroll to bring the comment into view
                    const scrollTop = commentElement.offsetTop - this.commentsContainer.offsetTop - 20;
                    this.commentsContainer.scrollTop = scrollTop;
                }
            }
        }, 150);
    }

    highlightComment (commentId){
        try{
            const commentElement = this.commentComponents.get(commentId).element;
            if(commentElement){
                // Briefly highlight the new comment
                commentElement.classList.add('ov_comment_just_added');
                setTimeout(() => {
                    commentElement.classList.remove('ov_comment_just_added');
                }, 5000);
            }
        }catch(error){
            console.error('Error highlighting comment', error);
        }
    }

    // Methods for external integration
    async addComment (message, cadReference = null)
    {
        debugLog('Adding comment externally');
        const currentUser = this.commentsManager.getCurrentUser();
        if (currentUser) {
            try {
                const commentData = {
                    userId: currentUser.id,
                    message
                };

                // Store cadReference in data field if provided
                if (cadReference) {
                    commentData.data = {
                        cadReference: cadReference
                    };
                }

                await this.commentsManager.addComment(commentData);
            } catch (error) {
                console.error('Failed to add comment externally:', error);
                throw error;
            }
        }
    }

    // Refresh comments from API
    async refreshComments ()
    {
        debugLog('Refreshing comments from API');
        try {
            const success = await this.commentsManager.refreshComments();
            if (success) {
                this.renderComments();
                this.showSuccessMessage('Comments refreshed successfully');
            }
            return success;
        } catch (error) {
            console.error('Failed to refresh comments:', error);
            this.showErrorMessage('Failed to refresh comments');
            return false;
        }
    }

    // Show connection status
    updateConnectionStatus ()
    {
        const isOnline = this.commentsManager.isOnline;
        const hasAPI = this.commentsManager.apiConfig.enabled;
        const pendingCount = this.commentsManager.pendingOperations.length;

        // Create or update status indicator
        if (!this.statusIndicator) {
            this.statusIndicator = AddDiv(this.headerDiv, 'ov_comments_status');
        }

        let statusText = '';
        let statusClass = '';

        if (hasAPI) {
            if (isOnline) {
                statusText = pendingCount > 0 ? `Online (${pendingCount} pending)` : 'Online';
                statusClass = pendingCount > 0 ? 'ov_status_syncing' : 'ov_status_online';
            } else {
                statusText = pendingCount > 0 ? `Offline (${pendingCount} pending)` : 'Offline';
                statusClass = 'ov_status_offline';
            }
        } else {
            statusText = 'Demo mode';
            statusClass = 'ov_status_demo';
        }

        this.statusIndicator.textContent = statusText;
        this.statusIndicator.className = `ov_comments_status ${statusClass}`;
    }

    showErrorMessage (message)
    {
        debugLog(`Showing error message: ${message}`);
        // Create temporary error notification
        const errorDiv = AddDiv(this.contentDiv, 'ov_comments_error_message');
        errorDiv.textContent = message;

        setTimeout(() => {
            if (errorDiv.parentNode) {
                errorDiv.parentNode.removeChild(errorDiv);
            }
        }, 5000);
    }

    showSuccessMessage (message)
    {
        debugLog(`Showing success message: ${message}`);
        // Create temporary success notification
        const successDiv = AddDiv(this.contentDiv, 'ov_comments_success_message');
        successDiv.textContent = message;

        setTimeout(() => {
            if (successDiv.parentNode) {
                successDiv.parentNode.removeChild(successDiv);
            }
        }, 3000);
    }

    getCommentsForCadElement (elementId)
    {
        debugLog(`Getting comments for CAD element ${elementId}`);
        return this.commentsManager.getCommentsForCadElement(elementId);
    }

    highlightCommentsForCadElement (elementId)
    {
        debugLog(`Highlighting comments for CAD element ${elementId}`);
        // Highlight comments that reference the given CAD element
        this.commentComponents.forEach((component, commentId) => {
            const comment = this.commentsManager.getComment(commentId);
            if (comment.cadReference && comment.cadReference.elementId === elementId) {
                component.element.classList.add('ov_comment_highlighted');
            } else {
                component.element.classList.remove('ov_comment_highlighted');
            }
        });
    }

    // Export/import for persistence
    exportComments ()
    {
        debugLog('Exporting comments');
        return this.commentsManager.exportComments();
    }

    importComments (data)
    {
        debugLog('Importing comments');
        this.commentsManager.importComments(data);
        this.renderComments();
    }

    toggleReplies (commentId)
    {
        debugLog(`Toggling replies for comment ${commentId}`);
        const repliesContainer = this.commentsContainer.querySelector(`[data-comment-id="${commentId}"]`);
        const commentComponent = this.commentComponents.get(commentId);

        if (repliesContainer && commentComponent) {
            const isCurrentlyHidden = repliesContainer.style.display === 'none';

            if (isCurrentlyHidden) {
                // Expand replies
                this.collapsedReplies.delete(commentId);
                this.explicitlyExpandedReplies.add(commentId);
                repliesContainer.style.display = 'block';
                commentComponent.setRepliesCount(commentComponent.repliesCount, false);
            } else {
                // Collapse replies
                this.collapsedReplies.add(commentId);
                this.explicitlyExpandedReplies.delete(commentId);
                repliesContainer.style.display = 'none';
                commentComponent.setRepliesCount(commentComponent.repliesCount, true);
            }
        }
    }

    showLoginRequired ()
    {
        debugLog('Showing login required message');

        // Clear existing content except header
        if (this.commentsContainer) {
            this.commentsContainer.remove();
            this.commentsContainer = null;
        }
        if (this.composerContainer) {
            this.composerContainer.remove();
            this.composerContainer = null;
        }
        if (this.loginContainer) {
            this.loginContainer.remove();
        }

        // Create login required container
        this.loginContainer = AddDiv(this.contentDiv, 'ov_comments_login_required');
        this.loginContainer.classList.add('ov_flex_grow');
        this.loginContainer.classList.add('ov_display_flex_column');
        this.loginContainer.classList.add('ov_justify_center');
        this.loginContainer.classList.add('ov_align_center');

        // Login icon
        const loginIcon = AddDiv(this.loginContainer, 'ov_comments_login_icon', '🔐');
        // loginIcon.textContent = '🔐';

        // Login message
        const loginMessage = AddDiv(this.loginContainer, 'ov_comments_login_message');
        loginMessage.textContent = Loc('Please log in to view and post comments');
        loginMessage.style.textAlign = 'center';
        loginMessage.style.marginBottom = '24px';
        loginMessage.style.color = '#666';

        // Login instructions
        const loginInstructions = AddDiv(this.loginContainer, 'ov_comments_login_instructions');
        if (this.commentsManager.apiConfig.enabled) {

        } else {
            loginInstructions.innerHTML = `
                ${Loc('Comments system is in demo mode.')}<br/>
                ${Loc('Please configure API settings to enable authentication.')}
            `;
        }
        loginInstructions.style.textAlign = 'center';
        loginInstructions.style.fontSize = '14px';
        loginInstructions.style.color = '#888';
        loginInstructions.style.lineHeight = '1.5';

        // Retry button (for API enabled mode)
        if (this.commentsManager.apiConfig.enabled) {
            // ? For external login link logic
            const redirectToLoginBtn = AddDiv(this.loginContainer, 'ov_button ov_button_small');
            redirectToLoginBtn.textContent = Loc('Redirect to Login');
            redirectToLoginBtn.style.marginTop = '20px';
            redirectToLoginBtn.addEventListener('click', async () => {
                debugLog('Redirect to login clicked');
                this.commentsManager.redirectToLogin();
            });

            // Magic link form section
            // const magicLinkSection = AddDiv(this.loginContainer, 'ov_comments_magic_link_section');
            // magicLinkSection.style.marginTop = '30px';
            // magicLinkSection.style.width = '100%';
            // magicLinkSection.style.maxWidth = '300px';

            // // Magic link title
            // const magicLinkTitle = AddDiv(magicLinkSection, 'ov_comments_magic_link_title');
            // magicLinkTitle.textContent = Loc('Or get a magic link');
            // magicLinkTitle.style.textAlign = 'center';
            // magicLinkTitle.style.marginBottom = '15px';
            // magicLinkTitle.style.fontSize = '16px';
            // magicLinkTitle.style.fontWeight = '600';
            // magicLinkTitle.style.color = '#333';

            // Magic link form
            // const magicLinkForm = AddDiv(magicLinkSection, 'ov_comments_magic_link_form');
            // magicLinkForm.style.display = 'flex';
            // magicLinkForm.style.flexDirection = 'column';
            // magicLinkForm.style.gap = '12px';

            // Email input
            // const emailInput = document.createElement('input');
            // emailInput.type = 'email';
            // emailInput.placeholder = Loc('Enter your email');
            // emailInput.className = 'ov_comments_magic_link_email';
            // emailInput.style.padding = '10px 12px';
            // emailInput.style.border = '1px solid #ddd';
            // emailInput.style.borderRadius = '4px';
            // emailInput.style.fontSize = '14px';
            // emailInput.style.width = '100%';
            // emailInput.style.boxSizing = 'border-box';
            // magicLinkForm.appendChild(emailInput);

            // // Magic link login button
            // const magicLinkBtn = AddDiv(magicLinkForm, 'ov_button ov_button_small');
            // magicLinkBtn.textContent = Loc('Send Magic Link');
            // magicLinkBtn.style.width = '100%';
            // magicLinkBtn.addEventListener('click', async () => {
            //     const email = emailInput.value.trim();
            //     if (!email) {
            //         this.showErrorMessage('Please enter a valid email address');
            //         return;
            //     }

            //     debugLog('Magic link requested for email:', email);
            //     try {
            //         await this.sendMagicLink(email);
            //         this.showSuccessMessage('Magic link sent! Check your email.');
            //         emailInput.value = '';
            //     } catch (error) {
            //         console.error('Failed to send magic link:', error);
            //         this.showErrorMessage('Failed to send magic link. Please try again.');
            //     }
            // });

            // // Handle Enter key for email input
            // emailInput.addEventListener('keydown', (e) => {
            //     if (e.key === 'Enter') {
            //         e.preventDefault();
            //         magicLinkBtn.click();
            //     }
            // });

            // ? For Internal login mechanic
            // const retryButton = AddDiv(this.loginContainer, 'ov_button ov_button_small');
            // retryButton.textContent = Loc('Retry Connection');
            // retryButton.style.marginTop = '20px';
            // retryButton.addEventListener('click', async () => {
            //     debugLog('Retry connection clicked');
            //     try {
            //         await this.commentsManager.initialize();
            //         this.refreshUI();
            //     } catch (error) {
            //         console.error('Failed to retry connection:', error);
            //         this.showErrorMessage('Failed to connect. Please check your credentials and try again.');
            //     }
            // });
        }
    }

    refreshUI ()
    {
        debugLog('Refreshing comments panel UI');

        // Clear all content except header
        if (this.commentsContainer) {
            this.commentsContainer.remove();
            this.commentsContainer = null;
        }
        if (this.composerContainer) {
            this.composerContainer.remove();
            this.composerContainer = null;
        }
        if (this.loginContainer) {
            this.loginContainer.remove();
            this.loginContainer = null;
        }

        // Re-initialize based on current authentication state
        const currentUser = this.commentsManager.getCurrentUser();
        debugLog("<<-- currentUser", currentUser);
        if (!currentUser) {
            this.showLoginRequired();
        } else {
            // Make sure contentDiv exists
            if (!this.contentDiv) {
                console.error('Comments panel contentDiv not available');
                return;
            }

            this.contentDiv.classList.add('ov_flex_grow');
            this.contentDiv.classList.add('ov_display_flex_column');

            // Header with user info and actions
            this.headerDiv = AddDiv(this.contentDiv, 'ov_comments_header');
            this.createHeader();

            // Update connection status
            this.updateConnectionStatus();
            // Recreate comments interface
            this.commentsContainer = AddDiv(this.contentDiv, 'ov_comments_container ov_thin_scrollbar');
            this.commentsContainer.classList.add('ov_flex_grow');
            this.commentsContainer.classList.add('ov_overflow_x_hidden');

            // Comment composer
            this.composerContainer = AddDiv(this.contentDiv, 'ov_comments_composer_container');
            this.mainComposer = new CommentComposer(this.commentsManager, async (data) => {
                if (data) {
                    debugLog('Main composer submitting comment', data);
                    try {
                        const commentData = {
                            userId: this.commentsManager.getCurrentUser()?.id,
                            message: data.message,
                            parentId: data.parentId
                        };

                        // Merge any data field from the composer
                        if (data.data) {
                            commentData.data = data.data;
                        }

                        await this.commentsManager.addComment(commentData);
                    } catch (error) {
                        console.error('Failed to add comment:', error);
                        this.showErrorMessage('Failed to add comment. Please try again.');
                    }
                }
            });

            if (this.composerContainer && this.mainComposer) {
                this.composerContainer.appendChild(this.mainComposer.createElement());
            }

            this.renderComments();
        }

        // Update header to reflect current state
        this.createHeader();
        this.updateConnectionStatus();
    }

    Clear ()
    {
        debugLog('Clearing comments panel');
        if (this.commentsContainer) {
            ClearDomElement(this.commentsContainer);
        }
        this.commentComponents.clear();
        this.collapsedReplies.clear();
        this.explicitlyExpandedReplies.clear();
        if (this.replyComposer) {
            this.replyComposer = null;
        }
    }

    async sendMagicLink (email)
    {
        debugLog('Sending magic link request for email:', email, "xx ", this.commentsManager.apiConfig);

        if (!this.commentsManager.apiConfig.magicLinkUrl) {
            throw new Error('API is not configured');
        }

        const currentUrl = window.location.href;
        const requestBody = {
            email: email,
            redirect_to: currentUrl
        };

        try {

            const response = await fetch(`${this.commentsManager.apiConfig.magicLinkUrl}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            debugLog('Magic link request successful:', result);
            return result;
        } catch (error) {
            debugLog('Magic link request failed:', error);
            throw error;
        }
    }

    Resize ()
    {
        debugLog('Resizing comments panel');
        // Handle panel resizing
        if (!this.headerDiv || !this.composerContainer || !this.contentDiv || !this.commentsContainer) {
            return;
        }

        const headerHeight = this.headerDiv.offsetHeight;
        const composerHeight = this.composerContainer.offsetHeight;
        const availableHeight = this.contentDiv.offsetHeight - headerHeight - composerHeight - 20;
        this.commentsContainer.style.height = availableHeight + 'px';
    }
}
